import { sendToApi } from 'api/client'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { AlmanacFile, MediaTransformations } from 'types'
import { reportError } from 'utils/error'

export interface BaseCreateMediaFileParams {
  source_url: string
  mime_type: string
  title: string
  project_key: string
  variants?: Array<MediaTransformations>
  show_in_file_system?: boolean
  extract_metadata_in_background_job?: boolean
}

interface CreateBrandKitMediaFileParams extends BaseCreateMediaFileParams {
  brand_kit_id: number
  campaign_id?: never
}

interface CreateCampaignFileParams extends BaseCreateMediaFileParams {
  brand_kit_id?: never
  campaign_id: number
}

export type CreateMediaFileParams = BaseCreateMediaFileParams | CreateBrandKitMediaFileParams | CreateCampaignFileParams

export const MediaFileClient = {
  create: (workspaceId: number, body: CreateMediaFileParams) => {
    return sendToApi(`/w/${workspaceId}/media_files`, {
      method: 'POST',
      body,
    })
  },
  getAlmanacFile: (key: string) => {
    return sendToApi<{ almanac_file: AlmanacFile }>(`/almanac_files/${key}/show_maximal`, {
      method: 'GET',
    })
  },
  update: (workspaceId: number, id: number, media_file: AlmanacFile) => {
    return sendToApi(`/w/${workspaceId}/media_files/${id}`, {
      method: 'PUT',
      body: {
        media_file,
      },
    })
  },
  destroy: (workspaceId: number, id: number) => {
    return sendToApi(`/w/${workspaceId}/media_files/${id}`, {
      method: 'DELETE',
    })
  },
  getSignedUrl: (workspaceId: number, project_key: string, filename: string) => {
    return sendToApi<{ pre_signed_url: string }>(`/w/${workspaceId}/media_files/pre_signed_url`, {
      method: 'POST',
      body: {
        filename,
        project_key,
      },
    })
  },
  uploadToS3: (preSignedUrl: string, file: File, onProgress?: (progress: number) => void) => {
    let retries = 0

    const onError = (
      reject: (reason?: any) => void,
      errorMessage: string,
      errorEvent: ProgressEvent<XMLHttpRequestEventTarget>,
      status: number,
      statusText: string,
      url: string,
    ) => {
      reportError(new Error(errorMessage), {
        severity: 'error',
        metaData: {
          error: errorEvent,
          status: status,
          statusText: statusText,
          url: url,
        },
      })
      reject(new Error(`${errorMessage}: ` + errorEvent))
    }

    const performUpload = (xhr: XMLHttpRequest) => {
      xhr.open('PUT', preSignedUrl, true)
      xhr.setRequestHeader('Content-Type', file.type)
      xhr.send(file)
    }

    return new Promise<Response>((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.upload?.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const pct = event.loaded / event.total
          onProgress?.(pct * 100)
        }
      })

      xhr.upload?.addEventListener('error', (error) => {
        if (retries < 3) {
          retries++
          showToastMessage(
            "Upload interrupted. We're automatically trying your video upload again. No need to do anything!",
            'info',
          )
          performUpload(xhr)
        } else {
          onError(reject, 'Upload failed', error, xhr.status, xhr.statusText, xhr.responseURL)
        }
      })

      xhr.upload?.addEventListener('abort', (error) => {
        onError(reject, 'Upload aborted', error, xhr.status, xhr.statusText, xhr.responseURL)
      })

      xhr.addEventListener('load', (_event) => {
        if ([200, 201].includes(xhr.status)) {
          const response = new Response()
          Object.defineProperty(response, 'url', { value: preSignedUrl })

          resolve(response)
        } else {
          onError(reject, 'Upload failed', xhr.response, xhr.status, xhr.statusText, xhr.responseURL)
        }
      })

      performUpload(xhr)
    })
  },
}
